import { css } from '@emotion/css';
import {
  ActionIcon,
  Box,
  Center,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSettings } from '@tabler/icons-react';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { useLoadCommunicationLocales } from 'modules/Settings/Configuration/api';
import { CommunicationLocalesModal } from 'modules/Settings/Configuration/CommunicationLocales';
import {
  useActiveChannelAtom,
  useTemplateLanguageAtom,
} from 'modules/Settings/Templates/TemplateEditor/state';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { isEmpty, isNullish } from 'remeda';
import { useLoadTemplateV2 } from 'shared/hooks/use-load-template-v2';
import {
  type RecommendedVariablesStatus,
  useRecommendedVariablesStatuses,
} from './use-missing-recommended-variables';

export const TemplateEditorLanguageBar = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const { template } = useLoadTemplateV2(templateId);
  const [templateLanguage, setTemplateLanguage] = useTemplateLanguageAtom();
  const [activeChannel] = useActiveChannelAtom();
  const statuses = useRecommendedVariablesStatuses();
  const { communicationLanguages: languages, isCommunicationLocalesLoading } =
    useLoadCommunicationLocales();
  const profile = useProfile();
  const [isLanguagesModalOpened, { open: openLanguagesModal, close: closeLanguagesModal }] =
    useDisclosure(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (isEmpty(languages)) return;
    if (languages.some(language => language.value === templateLanguage)) return;
    setTemplateLanguage(languages[0].value);
  }, [languages, templateLanguage, setTemplateLanguage]);

  const theme = useMantineTheme();

  if (isNullish(template)) return null;

  return (
    <Stack
      h="100%"
      w="100%"
      align="center"
      py="md"
      bg="gray.1"
      style={{ borderLeft: `1px solid ${theme.colors.gray[3]}` }}
    >
      <Stack w="100%" flex={1} px="xs" gap="xs">
        {isCommunicationLocalesLoading && <LanguageBarSkeleton />}
        {languages.map(lang => {
          const isActive = templateLanguage === lang.value;
          const status = activeChannel ? statuses[activeChannel]?.[lang.value] : undefined;
          return (
            <Center
              component="button"
              onClick={() => setTemplateLanguage(lang.value)}
              key={lang.value}
              p="xs"
              bdrs="md"
              className={getButtonClass(theme, isActive)}
            >
              <Stack gap={6} align="center">
                <Text
                  fz={16}
                  fw={600}
                  c={isActive ? 'purple.7' : 'dimmed'}
                  style={{ userSelect: 'none' }}
                >
                  {lang.value.toUpperCase()}
                </Text>
                <LanguageStatusDot status={status} />
              </Stack>
            </Center>
          );
        })}
      </Stack>
      {profile.isAdmin && (
        <>
          <Tooltip
            label={t(i18nKeys.FORM.PREFERENCES.COMMUNICATION_LANGUAGES.TITLE)}
            position="left"
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={openLanguagesModal}
              aria-label={t(i18nKeys.FORM.PREFERENCES.COMMUNICATION_LANGUAGES.TITLE)}
            >
              <IconSettings size={18} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <CommunicationLocalesModal
            isOpened={isLanguagesModalOpened}
            onClose={closeLanguagesModal}
          />
        </>
      )}
    </Stack>
  );
};

// Three is the usual number of languages; the blocks only hold the rail's shape until the real
// ones arrive.
const LanguageBarSkeleton = () => (
  <>
    {[0, 1, 2].map(index => (
      <Skeleton key={index} h={52} radius="md" />
    ))}
  </>
);

const STATUS_COLORS: Record<RecommendedVariablesStatus, string> = {
  complete: 'green.6',
  missing: 'orange.5',
  empty: 'red.8',
};

const STATUS_TOOLTIP_KEYS: Record<RecommendedVariablesStatus, string | null> = {
  complete: null,
  missing: i18nKeys.TEMPLATE.MISSING_VARIABLES_LANGUAGE,
  empty: i18nKeys.TEMPLATE.NO_CONTENT_LANGUAGE,
};

const LanguageStatusDot = ({ status }: { status: RecommendedVariablesStatus | undefined }) => {
  const { t } = useTranslation();

  if (isNullish(status)) return null;

  const dot = <Box w={8} h={8} bg={STATUS_COLORS[status]} style={{ borderRadius: '50%' }} />;
  const tooltipKey = STATUS_TOOLTIP_KEYS[status];

  return isNullish(tooltipKey) ? dot : <Tooltip label={t(tooltipKey)}>{dot}</Tooltip>;
};

const getButtonClass = (theme: ReturnType<typeof useMantineTheme>, isActive: boolean) => css`
  background-color: ${isActive ? theme.colors.purple[2] : 'transparent'};
  border: ${isActive ? `2px solid ${theme.colors.purple[3]}` : '2px solid transparent'};
  cursor: pointer;

  ${
    !isActive &&
    `&:hover {
    background-color: ${theme.colors.purple[0]};
    border: 2px solid ${theme.colors.gray[4]};
  }`
  }
`;
